import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'components/Modal/Modal';
import { wrapFunctionalComponent } from 'utils/framework';
import { getImageAltText } from 'utils/Accessibility';
import { space, colors, fontSizes } from 'style/config';
import {
    Grid, Box, Text, Divider, Image, Link, Flex
} from 'components/ui';
import Chevron from 'components/Chevron';
import StringUtils from 'utils/String';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import { useManageSubscriptionModal } from 'components/GlobalModals/ManageSubscriptionModal/hooks/useManageSubscriptionModal';

const SM_IMG_SIZE = 64;
const SM_IMG_GAP = space[2];

function ManageSubscriptionModal({
    currentSubscription,
    fireGenericErrorAnalytics,
    isOpen,
    onChangeFrequency,
    onChangePayment,
    onChangeShipping,
    onDismiss,
    onPauseSubscription,
    onSkipDelivery
}) {
    const {
        creditCard,
        featureFlags,
        isLoading,
        localization,
        onModalClose,
        product,
        shippingInfo,
        shouldShowDeliveryMessage,
        shouldShowItemVariation
    } = useManageSubscriptionModal({
        currentSubscription,
        onDismiss,
        fireGenericErrorAnalytics
    });

    return (
        <Modal
            width={0}
            onDismiss={onModalClose}
            isOpen={isOpen}
            isHidden={isLoading}
            isDrawer={true}
        >
            <Modal.Header>
                <Modal.Title children={localization.manageSubscription} />
            </Modal.Header>
            <Modal.Body>
                {product && (
                    <>
                        <Grid
                            gap={[`${SM_IMG_GAP}px`, 4]}
                            css={styles.productContainer}
                        >
                            <Box>
                                <ProductImage
                                    id={product.skuId}
                                    size={[SM_IMG_SIZE, 87]}
                                    skuImages={product.skuImages}
                                    disableLazyLoad={true}
                                    altText={getImageAltText(product)}
                                />
                            </Box>
                            <div>
                                <Grid
                                    css={styles.productDetails}
                                    minHeight={[SM_IMG_SIZE, 0]}
                                >
                                    <Box>
                                        <Text
                                            is='p'
                                            css={styles.title}
                                            children={product.brandName}
                                            data-at={Sephora.debug.dataAt('sku_brand')}
                                        />
                                        <Text
                                            is='p'
                                            css={styles.subtitle}
                                            children={product.productName}
                                            data-at={Sephora.debug.dataAt('sku_name')}
                                        />
                                        {shouldShowItemVariation && (
                                            <Text
                                                is='p'
                                                css={styles.detailsContainer}
                                                data-at={Sephora.debug.dataAt('sku_size')}
                                            >
                                                {`${product.variationType}: ${product.variationValue}`}
                                                <span css={styles.detailsSeparator}>•</span>
                                                {StringUtils.format(localization.itemText, product.skuId)}
                                            </Text>
                                        )}
                                        <Text
                                            is='p'
                                            css={styles.productQty}
                                            data-at={Sephora.debug.dataAt('sku_qty')}
                                        >
                                            {localization.itemQuantityText}
                                        </Text>
                                        {featureFlags.enableReplenishmentOperations && (
                                            <Box css={styles.linksContainer}>
                                                <Link
                                                    css={styles.blueLink}
                                                    onClick={onSkipDelivery}
                                                >
                                                    {localization.skip}
                                                </Link>
                                                <span css={styles.spacer}>|</span>
                                                <Link
                                                    css={styles.blueLink}
                                                    onClick={onPauseSubscription}
                                                >
                                                    {localization.pause}
                                                </Link>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </div>
                        </Grid>
                        <Divider />
                        <Grid
                            css={styles.tabContainer}
                            onClick={featureFlags.enableReplenishmentFrequencyModifiable ? onChangeFrequency : null}
                        >
                            <Flex flexDirection='column'>
                                <Text
                                    is='h3'
                                    css={styles.tabHeading}
                                >
                                    {localization.tabHeadingText}
                                </Text>
                                {shouldShowDeliveryMessage && <Text css={styles.detailsContainer}>{localization.askToUpdateDelivery}</Text>}
                            </Flex>
                            {featureFlags.enableReplenishmentFrequencyModifiable && (
                                <Chevron
                                    isThicker={true}
                                    css={styles.chevron}
                                    direction='right'
                                />
                            )}
                        </Grid>
                        <Divider />
                        <Grid
                            css={styles.tabContainer}
                            {...(featureFlags.enableReplenishmentAddressModifiable && { onClick: onChangeShipping })}
                        >
                            <Text
                                is='h3'
                                css={styles.tabHeading}
                            >
                                {localization.shippingTitle}
                            </Text>
                            <Box css={styles.address}>
                                <Text is='p'>{shippingInfo.fullName}</Text>
                                <Text is='p'>
                                    {shippingInfo.addressLine1}
                                    <Text
                                        is='span'
                                        css={styles.block}
                                    >
                                        {shippingInfo.fullAddress}
                                    </Text>
                                    <Text
                                        is='span'
                                        css={styles.block}
                                    >
                                        {shippingInfo.phoneNumber}
                                    </Text>
                                </Text>
                                {featureFlags.enableReplenishmentAddressModifiable && (
                                    <Chevron
                                        isThicker={true}
                                        css={styles.chevron}
                                        direction='right'
                                    />
                                )}
                            </Box>
                        </Grid>
                        <Divider />
                        <Grid
                            css={styles.lastTab}
                            onClick={featureFlags.enableReplenishmentPaymentModifiable ? onChangePayment : null}
                        >
                            <Text
                                is='h3'
                                css={{
                                    ...styles.tabHeading,
                                    ...styles.lastTabHeading
                                }}
                            >
                                {localization.paymentTitle}
                            </Text>
                            <Box>
                                <Image
                                    css={styles.creditCardIcon}
                                    height={32}
                                    width={50}
                                    src={creditCard.logo}
                                />
                                <Text
                                    is='p'
                                    css={styles.inlineBlock}
                                >
                                    {creditCard.title}
                                </Text>
                                {featureFlags.enableReplenishmentPaymentModifiable && (
                                    <Chevron
                                        isThicker={true}
                                        css={styles.chevron}
                                        direction='right'
                                    />
                                )}
                            </Box>
                        </Grid>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}

const styles = {
    productContainer: {
        gridTemplateColumns: 'auto 1fr',
        lineHeight: 'tight',
        marginTop: `${space[2]}px`,
        marginBottom: `${space[5]}px`,
        alignItems: 'start'
    },
    productDetails: {
        gap: `${space[2]}px`,
        gridTemplateColumns: 'auto 1fr'
    },
    tabContainer: {
        padding: `${space[3]}px ${space[5]}px ${space[3]}px 0`,
        gridTemplateColumns: 'auto 1fr',
        position: 'relative'
    },
    detailsContainer: {
        fontSize: `${fontSizes.sm}px`,
        color: `${colors.gray}`
    },
    productQty: {
        fontSize: `${fontSizes.sm}px`,
        lineHeight: `${fontSizes.xs}px`,
        marginTop: `${space[3]}px`
    },
    tabHeading: {
        fontSize: `${fontSizes.base}px`,
        fontWeight: 'bold'
    },
    address: {
        lineHeight: `${fontSizes.md}px`
    },
    lastTab: {
        marginTop: `${space[3]}px`,
        gridTemplateColumns: 'auto 1fr',
        position: 'relative'
    },
    lastTabHeading: {
        paddingTop: `${space[1]}px`
    },
    title: {
        fontSize: `${fontSizes.sm}px`,
        lineHeight: `${fontSizes.sm}px`,
        fontWeight: 'bold'
    },
    subtitle: {
        fontSize: `${fontSizes.sm}px`,
        marginBottom: `${space[1]}px`,
        lineHeight: `${space[4]}px`
    },
    detailsText: {
        display: 'inline-block',
        lineHeight: `${space[4]}px`
    },
    detailsSeparator: {
        margin: '0 .5em'
    },
    block: {
        display: 'block'
    },
    chevron: {
        position: 'absolute',
        top: '50%',
        right: '0',
        transform: 'translate(0, -50%)',
        pointerEvents: 'none'
    },
    inlineBlock: {
        display: 'inline-block'
    },
    creditCardIcon: {
        height: `${space[32]}px`,
        width: `${space[50]}px`,
        marginRight: `${space[3]}px`,
        verticalAlign: 'middle'
    },
    blueLink: {
        color: `${colors.blue}`,
        fontSize: `${fontSizes.sm}px`
    },
    linksContainer: {
        marginTop: `${space[3]}px`
    },
    spacer: {
        marginTop: `${space[3]}px`,
        color: `${colors.midGray}`,
        margin: `0 ${space[2]}px`
    }
};

ManageSubscriptionModal.propTypes = {
    currentSubscription: PropTypes.shape({
        frequency: PropTypes.number,
        frequencyType: PropTypes.string,
        items: PropTypes.array,
        paymentId: PropTypes.string,
        profileId: PropTypes.string,
        shippingAddressId: PropTypes.string
    }).isRequired,
    isOpen: PropTypes.bool.isRequired,
    onChangeFrequency: PropTypes.func.isRequired,
    onChangePayment: PropTypes.func.isRequired,
    onChangeShipping: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onPauseSubscription: PropTypes.func.isRequired,
    onSkipDelivery: PropTypes.func.isRequired,
    fireGenericErrorAnalytics: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(ManageSubscriptionModal, 'ManageSubscriptionModal');
